import { useState, useEffect, useCallback, type ClipboardEvent } from 'react'
import { toPng } from 'html-to-image'
import { Button } from '@merels/ui'
import { useTheme } from '../../../../contexts/ThemeContext'
import { useWebContainer } from '../../../providers/DepProvider/DepProvider'
import { ListDetailView } from '../../library/ListDetailView/ListDetailView'
import { captureIssueHistory } from './issueHistory'

/**
 * Component props for ScreenshotDebug
 */
interface ScreenshotDebugProps {
  onActiveIssueChange?: (issue: any | null, onCopyLink?: () => void) => void
  activeIssueProp?: any | null
}

const SVG_PRESENTATION_PROPERTIES = [
  'fill',
  'stroke',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-dasharray',
  'stroke-dashoffset',
  'opacity',
  'filter',
  'clip-path',
] as const

function copySvgPresentationStyles(source: HTMLElement, clone: HTMLElement): void {
  const sourceElements = Array.from(source.querySelectorAll<SVGElement>('svg, svg *'))
  const clonedElements = Array.from(clone.querySelectorAll<SVGElement>('svg, svg *'))

  sourceElements.forEach((sourceElement, index) => {
    const clonedElement = clonedElements[index]
    if (!clonedElement) return

    const computedStyle = window.getComputedStyle(sourceElement)
    SVG_PRESENTATION_PROPERTIES.forEach((property) => {
      clonedElement.style.setProperty(property, computedStyle.getPropertyValue(property))
    })
  })
}

async function dataUrlFromBlob(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

async function embedSvgImages(source: HTMLElement, clone: HTMLElement): Promise<void> {
  const sourceImages = Array.from(source.querySelectorAll<SVGImageElement>('svg image'))
  const clonedImages = Array.from(clone.querySelectorAll<SVGImageElement>('svg image'))

  await Promise.all(sourceImages.map(async (sourceImage, index) => {
    const clonedImage = clonedImages[index]
    const href = sourceImage.getAttribute('href')
    if (!clonedImage || !href || href.startsWith('data:')) return

    try {
      const response = await fetch(new URL(href, document.baseURI))
      if (!response.ok) return

      clonedImage.setAttribute('href', await dataUrlFromBlob(await response.blob()))
    } catch {
      // Keep the original URL when an asset cannot be embedded.
    }
  }))
}

async function prepareBoardForCapture(boardElement: HTMLElement): Promise<() => void> {
  const svgElements = Array.from(boardElement.querySelectorAll<SVGElement>('svg, svg *'))
  const svgImages = Array.from(boardElement.querySelectorAll<SVGImageElement>('svg image'))
  const originalStyles = svgElements.map((element) => element.getAttribute('style'))
  const originalHrefs = svgImages.map((image) => image.getAttribute('href'))

  copySvgPresentationStyles(boardElement, boardElement)
  await embedSvgImages(boardElement, boardElement)

  return () => {
    svgElements.forEach((element, index) => {
      const originalStyle = originalStyles[index]
      if (originalStyle === null) {
        element.removeAttribute('style')
      } else {
        element.setAttribute('style', originalStyle)
      }
    })
    svgImages.forEach((image, index) => {
      const originalHref = originalHrefs[index]
      if (originalHref === null) {
        image.removeAttribute('href')
      } else {
        image.setAttribute('href', originalHref)
      }
    })
  }
}

/** Returns the app-configured worker URL required for issue capture requests. */
function requireWorkerUrl(workerUrl: string | undefined): string {
  if (!workerUrl) {
    throw new Error('Issue capture requires a configured worker URL.')
  }
  return workerUrl
}

export function ScreenshotDebug({ onActiveIssueChange, activeIssueProp }: ScreenshotDebugProps = {}) {
  const [screenshot, setScreenshot] = useState<string | null>(null)
  const [issuesList, setIssuesList] = useState<any[]>([])
  const [isCapturing, setIsCapturing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  
  // Local description state to avoid cursor jumps during typing
  const [localDescription, setLocalDescription] = useState('')

  const { getAppColor } = useTheme()
  const appColorShades = getAppColor()
  const container = useWebContainer()

  const activeGame = container.roomState.activeGame.value
  const gameId = activeGame?.gameId || 'unknown'

  const activeIssue = activeIssueProp || null

  // Fetch the list of issues for the current game
  const fetchIssuesList = useCallback(async () => {
    try {
      const workerUrl = requireWorkerUrl(container.roomConnection._workerUrl)
      const res = await fetch(`${workerUrl}/api/issues/${encodeURIComponent(gameId)}`)
      if (res.ok) {
        const data = (await res.json()) as { issues: any[] }
        setIssuesList(data.issues || [])
      }
    } catch (err) {
      console.error('[ScreenshotDebug] failed to fetch issues list:', err)
    }
  }, [container.roomConnection._workerUrl, gameId])

  // Sync prop changes for list reset and local descriptions
  useEffect(() => {
    if (activeIssueProp === null) {
      setScreenshot(null)
      fetchIssuesList()
    } else if (activeIssueProp) {
      setLocalDescription(activeIssueProp.description || '')
      if (activeIssueProp.imageUrl) {
        const workerUrl = container.roomConnection._workerUrl
        setScreenshot(workerUrl ? `${workerUrl}${activeIssueProp.imageUrl}` : null)
      }
    }
  }, [activeIssueProp, fetchIssuesList, container.roomConnection._workerUrl])

  // Load issues list on mount or when game changes
  useEffect(() => {
    fetchIssuesList()
  }, [fetchIssuesList])

  const handleCopyMarkdownLink = async (issueToCopy = activeIssue) => {
    if (!issueToCopy) return
    
    try {
      const workerUrl = requireWorkerUrl(container.roomConnection._workerUrl)
      const issueId = issueToCopy.issueId || issueToCopy.issue_id
      const linkStr = `${workerUrl}/api/issues/markdown/${encodeURIComponent(gameId)}/${encodeURIComponent(issueId)}`
      await navigator.clipboard.writeText(linkStr)
    } catch (err) {
      console.error('[ScreenshotDebug] Failed to copy markdown URL:', err)
    }
  }

  const handleCapture = useCallback(async () => {
    setIsCapturing(true)
    setErrorMessage(null)
    setScreenshot(null)
    onActiveIssueChange?.(null)

    try {
      // Find the main board game container
      const boardElement = document.querySelector('[data-testid="game-board-container"]') as HTMLElement
      if (!boardElement) {
        throw new Error('Playarea container (game-board-container) not found in the DOM.')
      }

      const restoreBoard = await prepareBoardForCapture(boardElement)
      let dataUrl: string
      try {
        dataUrl = await toPng(boardElement, {
          cacheBust: true,
          style: {
            backgroundColor: appColorShades.background,
          },
        })
      } finally {
        restoreBoard()
      }

      setScreenshot(dataUrl)

      // Automatically compile metadata & upload to worker
      const workerUrl = requireWorkerUrl(container.roomConnection._workerUrl)
      const gameVersion = activeGame?.gameVersion?.versionId || 'unknown'
      const roomName = container.roomLink.roomName || 'local'
      const merelsBuild = container.debugPanelVM.deploymentInfo.value?.commitHash || 'unknown'
      const gameSettings = activeGame?.gameOptions || {}
      const hintsEnabled = container.shellVM.game.hintsEnabled.value
      const isRulebookMode = container.shellVM.game.viewMode.value === 'rulebook'
      
      // Determine user identity
      const user = container.auth.currentUser.value
      const userEmail = user?.primaryEmailAddress?.emailAddress
      const capturedBy = userEmail ? userEmail : `Guest: ${container.debugPanelVM.ourMateId.value || 'unknown'}`

      const { lastStates, lastActions } = captureIssueHistory(
        container.moveHistory.entries.value,
        activeGame?.gameState,
        (activeGame?.initialState as { G?: unknown } | undefined)?.G,
      )

      // Post the new issue to the server
      const res = await fetch(`${workerUrl}/api/issues`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          gameId,
          gameVersion,
          roomName,
          merelsBuild,
          gameSettings,
          hintsEnabled,
          isRulebookMode,
          capturedBy,
          lastStates,
          lastActions,
          imageData: dataUrl,
          description: '',
        }),
      })

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`Failed to save screenshot on server (${res.status}): ${text}`)
      }

      const responseData = (await res.json()) as { success: boolean; issue: any }
      
      // Automatically copy markdown link to clipboard
      await handleCopyMarkdownLink(responseData.issue)

      onActiveIssueChange?.(responseData.issue, () => handleCopyMarkdownLink(responseData.issue))
      
      // Refresh list in background
      fetchIssuesList()
    } catch (err) {
      console.error('[ScreenshotDebug] capture error:', err)
      setErrorMessage((err as Error).message || 'Failed to capture and upload screenshot.')
    } finally {
      setIsCapturing(false)
    }
  }, [
    activeGame,
    gameId,
    container,
    onActiveIssueChange,
    fetchIssuesList,
    appColorShades.background
  ])

  // Listen for Cmd-P / Ctrl-P shortcut capture command
  const triggerCapture = container.debugPanelVM.triggerCaptureScreenshot.value
  useEffect(() => {
    if (triggerCapture) {
      container.debugPanelVM.clearCaptureScreenshot()
      handleCapture()
    }
  }, [triggerCapture, container.debugPanelVM, handleCapture])

  // PATCH changes to the server
  const handleUpdateField = async (fields: { description?: string; status?: string; resolution?: string | null }) => {
    if (!activeIssue) return
    setIsSaving(true)
    setSaveSuccess(false)
    setErrorMessage(null)

    const issueId = activeIssue.issueId || activeIssue.issue_id

    try {
      const workerUrl = requireWorkerUrl(container.roomConnection._workerUrl)
      const res = await fetch(`${workerUrl}/api/issues/${encodeURIComponent(gameId)}/${encodeURIComponent(issueId)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(fields),
      })

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`Failed to update issue (${res.status}): ${text}`)
      }

      // Update parent state
      const updatedIssue = {
        ...activeIssue,
        ...fields,
        ...(fields.resolution ? { status: 'closed' } : {}),
        ...(fields.status && fields.status !== 'closed' ? { resolution: null } : {}),
      }
      onActiveIssueChange?.(updatedIssue, () => handleCopyMarkdownLink(updatedIssue))
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 1500)
      
      // Refresh list in background
      fetchIssuesList()
    } catch (err) {
      console.error('[ScreenshotDebug] field update error:', err)
      setErrorMessage((err as Error).message || 'Failed to sync changes with the server.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSelectIssue = async (issue: any) => {
    setErrorMessage(null)
    setScreenshot(null)
    
    // Set basic fields first
    onActiveIssueChange?.(issue, () => handleCopyMarkdownLink(issue))

    // Load full issue details (to get imageData/imageUrl preview if it exists)
    const issueId = issue.issueId || issue.issue_id
    try {
      const workerUrl = requireWorkerUrl(container.roomConnection._workerUrl)
      const res = await fetch(`${workerUrl}/api/issues/${encodeURIComponent(gameId)}/${encodeURIComponent(issueId)}`)
      if (res.ok) {
        const data = (await res.json()) as { issue: any }
        onActiveIssueChange?.(data.issue, () => handleCopyMarkdownLink(data.issue))
        if (data.issue.imageUrl) {
          setScreenshot(`${workerUrl}${data.issue.imageUrl}`)
        }
      }
    } catch (err) {
      console.error('[ScreenshotDebug] failed to fetch full issue details:', err)
    }
  }

  const handlePasteImage = async (event: ClipboardEvent<HTMLDivElement>) => {
    const image = Array.from(event.clipboardData.items)
      .find((item) => item.type.startsWith('image/'))
      ?.getAsFile()
    if (!image || !activeIssue) return

    event.preventDefault()
    setIsSaving(true)
    setSaveSuccess(false)
    setErrorMessage(null)

    try {
      const workerUrl = requireWorkerUrl(container.roomConnection._workerUrl)
      const issueId = activeIssue.issueId || activeIssue.issue_id
      const imageData = await dataUrlFromBlob(image)
      const res = await fetch(
        `${workerUrl}/api/issues/${encodeURIComponent(gameId)}/${encodeURIComponent(issueId)}/images`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageData }),
        },
      )
      if (!res.ok) {
        throw new Error(`Failed to add image (${res.status}): ${await res.text()}`)
      }

      const { imageUrl } = await res.json() as { imageUrl: string }
      const imageUrls = activeIssue.imageUrls || (activeIssue.imageUrl ? [activeIssue.imageUrl] : [])
      const updatedIssue = { ...activeIssue, imageUrls: [...imageUrls, imageUrl] }
      onActiveIssueChange?.(updatedIssue, () => handleCopyMarkdownLink(updatedIssue))
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 1500)
    } catch (err) {
      console.error('[ScreenshotDebug] image paste error:', err)
      setErrorMessage((err as Error).message || 'Failed to add pasted image.')
    } finally {
      setIsSaving(false)
    }
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border border-yellow-500/20'
      case 'to-do':
        return 'bg-gray-500/10 text-gray-500 border border-basic'
      case 'in-progress':
        return 'bg-blue-500/10 text-blue-500 border border-blue-500/20'
      case 'closed':
        return 'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20'
      default:
        return 'bg-muted-bg text-muted border border-basic'
    }
  }

  return (
    <ListDetailView
      items={issuesList}
      activeItem={activeIssue}
      onBack={() => onActiveIssueChange?.(null)}
      errorMessage={errorMessage}
      emptyMessage="No captured issues for this game yet."
      className="max-h-[85vh] overflow-y-auto pr-1"
      listHeader={
        <Button
          onClick={handleCapture}
          disabled={isCapturing}
          variant="default"
          className="w-full relative overflow-hidden group py-2"
          style={{
            backgroundColor: appColorShades.main,
            color: '#ffffff',
          }}
        >
          {isCapturing ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Capturing...
            </span>
          ) : (
            'Capture'
          )}
        </Button>
      }
      renderListItem={(issue, _index, registerRef) => {
        const id = issue.issueId || issue.issue_id
        const date = issue.createdAt || issue.created_at
        const workerUrl = container.roomConnection._workerUrl
        const thumbnailSrc = workerUrl && issue.imageUrl ? `${workerUrl}${issue.imageUrl}` : null

        return (
          <div
            key={id}
            ref={registerRef}
            onClick={() => handleSelectIssue(issue)}
            data-list-item="true"
            tabIndex={0}
            className="w-full hover:bg-muted-bg/25 focus:bg-muted-bg/35 focus:outline-none cursor-pointer flex items-center justify-between transition-all duration-150 h-12 border-b border-basic/40 last:border-0"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0 h-full">
              {/* Left: Thumbnail */}
              <div className="w-12 h-full flex-shrink-0 bg-gray-900/10 dark:bg-gray-100/10 overflow-hidden flex items-center justify-center">
                {thumbnailSrc ? (
                  <img
                    src={thumbnailSrc}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="opacity-40"
                  >
                    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                  </svg>
                )}
              </div>

              {/* Middle: Info */}
              <div className="flex-1 min-w-0 flex flex-col justify-between py-1.5 pr-1">
                <div className="text-xs truncate font-medium">
                  <span className="text-accent font-mono mr-1.5 font-semibold">#{id}</span>
                  <span className="text-primary font-normal">
                    {issue.description || <span className="italic opacity-55">No description</span>}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-muted">
                  <span>{new Date(date).toLocaleDateString()}</span>
                  <span className={`px-1 py-0.5 rounded-sm uppercase tracking-wider text-[8px] font-bold ${getStatusBadgeClass(issue.status)}`}>
                    {issue.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Chevron */}
            <div className="flex-shrink-0 text-muted-foreground opacity-45 pr-2.5 pl-1 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </div>
          </div>
        )
      }}
      renderDetails={() => (
        <>
          {/* Screenshot Preview */}
          {screenshot && (
            <div className="relative rounded overflow-hidden max-h-[220px] flex items-center justify-center bg-gray-900/5 dark:bg-gray-100/5">
              <img
                src={screenshot}
                alt=""
                className="max-w-full max-h-[220px] object-contain rounded"
              />
            </div>
          )}

          {activeIssue.imageUrls?.slice(activeIssue.imageUrl ? 1 : 0).map((imageUrl: string, index: number) => {
            const workerUrl = container.roomConnection._workerUrl
            return (
              <div
                key={imageUrl}
                className="relative rounded overflow-hidden max-h-[220px] flex items-center justify-center bg-gray-900/5 dark:bg-gray-100/5"
              >
                <img
                  src={workerUrl ? `${workerUrl}${imageUrl}` : imageUrl}
                  alt={`Additional issue screenshot ${index + 1}`}
                  className="max-w-full max-h-[220px] object-contain rounded"
                />
              </div>
            )
          })}

          <div
            tabIndex={0}
            onPaste={handlePasteImage}
            className="rounded border border-dashed border-basic px-3 py-2 text-xs text-muted focus:outline-none focus:ring-1 focus:ring-accent"
          >
            Click here, then paste an image to attach it to this issue.
          </div>

          {/* Ticket Editor Form */}
          <div className="flex flex-col gap-3">
            {/* Inline controls */}
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="text-muted">Status:</span>
                <select
                  id="issue-status"
                  className="bg-transparent text-primary font-medium focus:outline-none cursor-pointer hover:text-accent border-0"
                  value={activeIssue.status}
                  onChange={(e) => handleUpdateField({ status: e.target.value })}
                >
                  <option value="new">new</option>
                  <option value="to-do">to-do</option>
                  <option value="in-progress">in-progress</option>
                  <option value="closed">closed</option>
                </select>
              </div>

              <div className="flex items-center gap-1.5">
                <span className="text-muted">Resolution:</span>
                <select
                  id="issue-resolution"
                  className="bg-transparent text-primary font-medium focus:outline-none cursor-pointer hover:text-accent border-0"
                  value={activeIssue.resolution || ''}
                  onChange={(e) => handleUpdateField({ resolution: e.target.value === '' ? null : e.target.value })}
                >
                  <option value="">none</option>
                  <option value="fixed">fixed</option>
                  <option value="wont-fix">wont-fix</option>
                  <option value="duplicate">duplicate</option>
                  <option value="invalid">invalid</option>
                </select>
              </div>

              <div className="text-[10px] text-muted flex-grow text-right h-4 flex items-center justify-end">
                {isSaving && <span className="animate-pulse">Syncing...</span>}
                {saveSuccess && <span className="text-accent font-medium">✓ Saved</span>}
              </div>
            </div>

            {/* Description Area */}
            <div className="flex flex-col gap-1">
              <textarea
                id="issue-description"
                className="w-full text-xs p-2 rounded border border-basic bg-background text-primary focus:outline-none focus:ring-1 focus:ring-accent min-h-[120px] resize-y"
                placeholder="Describe what went wrong..."
                value={localDescription}
                onChange={(e) => setLocalDescription(e.target.value)}
                onBlur={(e) => handleUpdateField({ description: e.target.value })}
              />
            </div>
          </div>
        </>
      )}
    />
  )
}
